import {
    useState,
    useEffect,
    useContext,
    useRef,
    useMemo,
    useCallback,
} from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import styles from './emergencyAlert.module.css';
import { useIntl } from 'react-intl';
import PageTitle from '../../layout/PageTitle/pageTitle';
import AIEduButton from '../../components/Button/AIEduButton';
import AIEduDialog from '../../components/Dialog/AIEduDialog';
import RegenerateKeyDialog from './components/RegenerateKeyDialog';
import AIEduToast from '../../components/Toast/AIEduToast';
import AIEduSpinner from '../../components/Spinner/AIEduSpinner';
import { Context } from '../../context/Context';
import EmergencyAlertList from './components/EmergencyAlertList';
import EmergencyAlertForm from './components/EmergencyAlertForm';
import EmergencyAlertMetadata from './components/EmergencyAlertMetadata';
import DeleteEmergencyAlertDialog from './components/DeleteEmergencyAlertDialog';
import TemplateGrid from './components/TemplateGrid';
import TagManagement from './components/TagManagement';
import TagManagementPopup from './components/TagManagementPopup';
import {
    PROVIDERS,
    ALERT_TYPES,
    COLOR_OPTIONS,
    PROVIDERS_MAP,
    UPDATED_AT,
} from './constants';
import { useAuthenticate } from '../../hooks';
import { API } from '../../services';
import {
    setToastComponent,
    showErrorToast,
    showSuccessToast,
} from '../../services/setup/ApiResponseHandler';
import { camelCaseToUpperCaseUnderscore, formatDateWithTimeStamp } from '../../utils/Utils';
import {
    EMERGENCY_ALERT_CREATE_PATH,
    EMERGENCY_ALERT_PATH,
} from '../../router/RouteConstants';
import { ASC, DESC } from '../../utils/Constants';

const EmergencyAlert = () => {
    const intl = useIntl();
    const { formatMessage: f } = intl;
    const { refreshMap, triggerRefresh } = useContext(Context);
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Determine the mode based on the current route
    const isCreateMode = location.pathname === EMERGENCY_ALERT_CREATE_PATH;
    const isEditMode = location.pathname.includes(EMERGENCY_ALERT_PATH) && id;
    const isListMode = !isCreateMode && !isEditMode;
    const [searchTerm, setSearchTerm] = useState('');
    const [shouldSearch, setShouldSearch] = useState(false);
    const [selectedAlertsList, setSelectedAlertsList] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(isCreateMode ? PROVIDERS[0] : null);
    const [selectedAlertType, setSelectedAlertType] = useState(null);
    const [xmlBodyText, setXmlBodyText] = useState('');
    const [selectedBackgroundColor, setSelectedBackgroundColor] = useState(null);
    const [selectedTextColor, setSelectedTextColor] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [originalBackgroundColor, setOriginalBackgroundColor] = useState(null);
    const [originalTextColor, setOriginalTextColor] = useState(null);
    const [originalTemplateId, setOriginalTemplateId] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [alertsLoading, setAlertsLoading] = useState(false);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [alertDataLoading, setAlertDataLoading] = useState(false);
    const [alertTitleText, setAlertTitleText] = useState('');
    const [descriptionText, setDescriptionText] = useState('');
    const [showTemplatePreview, setShowTemplatePreview] = useState(false);
    const [isAlertTitleEditMode, setIsAlertTitleEditMode] = useState(false);
    const [isDescriptionEditMode, setIsDescriptionEditMode] = useState(false);
    const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [currentAlertData, setCurrentAlertData] = useState(null);
    const [showTagManagementPopup, setShowTagManagementPopup] = useState(false);
    const [pendingSaveAction, setPendingSaveAction] = useState(null);
    const [showRegenerateKeyDialog, setShowRegenerateKeyDialog] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);
    const [selectedOperator, setSelectedOperator] = useState('or');

    // Pagination state
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [sortBy, setSortBy] = useState(UPDATED_AT);
    const [sortOrder, setSortOrder] = useState(-1); // -1 for desc, 1 for asc

    const { accountInfo } = useAuthenticate();
    const { organizations } = accountInfo ?? {};
    const { organizationId } = organizations?.[0] ?? {};
    const toastRef = useRef(null);

    useEffect(() => {
        setToastComponent(toastRef.current);
    }, []);

    // Fetch alerts from API using getAllAlerts with pagination
    const fetchAlerts = async (
        start = 0,
        rowsPerPage = rows,
        sortByField = sortBy,
        sort = sortOrder,
        search = searchTerm
    ) => {
        if (!organizationId) {
            showErrorToast(
                f({ id: 'AI_EDU_ORGANIZATION_ID_REQUIRED_TO_FETCH_ALERTS' })
            );
            return;
        }

        setAlertsLoading(true);
        try {
            const params = {
                start,
                rowsPerPage,
                search: search || undefined,
                sortBy: camelCaseToUpperCaseUnderscore(sortByField),
                sort: sort === 1 ? ASC : DESC,
            };

            const { data } = await API.getAllAlerts(organizationId, params);
            const { alerts, pagination } = data ?? {};
            setAlerts(alerts || []);
            setTotalRecords(pagination?.total || 0);
        } catch (error) {
            showErrorToast(f({ id: 'AI_EDU_ERROR_FETCHING_EMERGENCY_ALERTS' }));
            setAlerts([]);
            setTotalRecords(0);
        } finally {
            setAlertsLoading(false);
        }
    };

    // Fetch templates from API using getAllAlertTemplates
    const fetchTemplates = async () => {
        if (!organizationId) {
            // showErrorToast('Organization ID is required to fetch templates');
            return;
        }

        setTemplatesLoading(true);
        try {
            const { data, status } = await API.getAllAlertTemplates(
                organizationId
            );

            if (status === 200) {
                setTemplates(data.templates);
                // Auto-select first template in create mode if no template is selected
                if (isCreateMode && data.templates && data.templates.length > 0 && !selectedTemplate) {
                    setSelectedTemplate(data.templates[0].templateId);
                    // Set default colors when template is auto-selected
                    setSelectedBackgroundColor({ name: 'White', code: '#FFFFFF' });
                    setSelectedTextColor({ name: 'Black', code: '#000000' });
                }
            } else {
                showErrorToast(f({ id: 'AI_EDU_FAILED_TO_FETCH_TEMPLATES' }));
            }
        } catch (error) {
            showErrorToast(f({ id: 'AI_EDU_ERROR_FETCHING_TEMPLATES' }));
        } finally {
            setTemplatesLoading(false);
        }
    };

    useEffect(() => {
        if (isCreateMode || isEditMode) {
            fetchTemplates();
        }
    }, [isCreateMode, isEditMode, organizationId]);

    // Fetch alert data when in edit mode
    const fetchAlertById = async () => {
        if (!organizationId || !id || !isEditMode) {
            return;
        }

        setAlertDataLoading(true);
        try {
            const { data, status } = await API.getAlertById(organizationId, id);
            const {
                title,
                description,
                alertProvider,
                alertType,
                xmlBody,
                templateId,
                bgColor,
                textColor,
                tags,
                operator,
                webHookUrl,
                apiKey,
            } = data ?? {};
            if (status === 200) {
                // Store the complete alert data for metadata display
                setCurrentAlertData(data);

                // Set form fields with the alert data
                setAlertTitleText(title || '');
                setDescriptionText(description || '');
                setSelectedProvider(
                    PROVIDERS.find((p) => p.code === alertProvider) || null
                );
                setSelectedAlertType(
                    ALERT_TYPES.find((t) => t.code === alertType) || null
                );
                setXmlBodyText(xmlBody || '');
                setSelectedTemplate(templateId || null);
                setOriginalTemplateId(templateId || null);
                const bgColorOption = COLOR_OPTIONS.find((c) => c.code === bgColor) || null;
                const textColorOption = COLOR_OPTIONS.find((c) => c.code === textColor) || null;
                setSelectedBackgroundColor(bgColorOption);
                setSelectedTextColor(textColorOption);
                setOriginalBackgroundColor(bgColorOption);
                setOriginalTextColor(textColorOption);
                
                // Set tags and operator
                setSelectedTags(tags || []);
                setSelectedOperator(operator || 'or');
                
                // Set webhook URL and API key
                setWebhookUrl(webHookUrl || '');
                setApiKey(apiKey || '');
            }
        } catch (error) {
            showErrorToast(f({ id: 'AI_EDU_ERROR_FETCHING_ALERT_DATA' }));
            navigate(EMERGENCY_ALERT_PATH);
        } finally {
            setAlertDataLoading(false);
        }
    };

    useEffect(() => {
        if (isEditMode && organizationId) {
            fetchAlertById();
        }
    }, [isEditMode, organizationId]);

    useEffect(() => {
        if (isListMode && organizationId) {
            fetchAlerts(Math.floor(first / rows), rows, sortBy, sortOrder, searchTerm);
        }
    }, [
        isListMode,
        organizationId,
        first,
        rows,
        sortBy,
        sortOrder,
        shouldSearch,
    ]);

    // Initial load for list mode
    useEffect(() => {
        if (isListMode && organizationId && !shouldSearch) {
            setShouldSearch(true); // Trigger initial load
        }
    }, [isListMode, organizationId]);

    useEffect(() => {
        // Reset all component state to initial values
        setIsDescriptionEditMode(false);
        setIsAlertTitleEditMode(false);
        setDescriptionText('');
        setAlertTitleText('');
        setSearchTerm('');
        setSelectedAlertsList([]);
        setSelectedProvider(isCreateMode ? PROVIDERS[0] : null);
        setSelectedAlertType(null);
        setXmlBodyText('');
        setSelectedBackgroundColor(null);
        setSelectedTextColor(null);
        setSelectedTemplate(null);
        setOriginalTemplateId(null);
        setOriginalBackgroundColor(null);
        setOriginalTextColor(null);
        setTemplates([]);
        setShowTemplatePreview(false);
        setLoading(false);
        setCurrentAlertData(null);
        setSelectedTags([]);
        setSelectedOperator('or');
        setWebhookUrl('');
        setApiKey('');
        
        // Refresh alerts list when in list mode
        if (isListMode && organizationId) {
            setFirst(0);
            setRows(10);
            fetchAlerts(0, 10, sortBy, sortOrder, searchTerm);
        }
    }, [refreshMap.alert, isListMode, isCreateMode, organizationId]);

    const handleTemplateSelect = useCallback(
        (templateId) => {
            // Prevent unchecking of already selected template
            if (templateId === selectedTemplate) {
                return; // Do nothing if trying to uncheck the current template
            }
            
            setSelectedTemplate(templateId);
            setShowTemplatePreview(false);

            if (isEditMode && templateId === originalTemplateId) {
                // In edit mode, if reselecting the original template, restore original colors
                setSelectedBackgroundColor(originalBackgroundColor);
                setSelectedTextColor(originalTextColor);
            } else {
                // New template selected, show only default colors
                setSelectedBackgroundColor({ name: 'White', code: '#FFFFFF' });
                setSelectedTextColor({ name: 'Black', code: '#000000' });
            }
        },
        [selectedTemplate, isEditMode, originalTemplateId, originalBackgroundColor, originalTextColor]
    );

    const handleTemplatePreview = useCallback(() => {
        if (selectedTemplate) {
            setShowTemplatePreview(!showTemplatePreview);
        }
    }, [selectedTemplate, showTemplatePreview]);

    const TemplatePreviewTitle = useCallback(() => {
        return (
            <div className='flex justify-content-between'>
                <div>{f({ id: 'AI_EDU_PREVIEW_TITLE' })}</div>
                <i
                    className='pi pi-times'
                    style={{ cursor: 'pointer' }}
                    onClick={hideTemplatePreviewDialog}
                />
            </div>
        );
    }, [f]);

    const hideTemplatePreviewDialog = () => {
        setShowTemplatePreview(false);
    };

    // Memoized template preview content to avoid expensive string replacements on every render
    const getTemplatePreviewContent = useMemo(() => {
        const template = templates.find(
            (t) => t.templateId === selectedTemplate
        );
        if (!template) return null;

        const bgColor = selectedBackgroundColor?.code || '#ffffff';
        const textColor = selectedTextColor?.code || '#000000';

        // Predefined style replacements for better performance
        const styleReplacements = [
            {
                className: 'alert-container',
                style: `background-color: ${bgColor}; color: ${textColor};`,
            },
            { className: 'alert-label', style: `color: ${textColor};` },
        ];

        // Apply all style replacements efficiently, appending to existing styles
        let modifiedTemplateInfo = template.templateInfo;
        styleReplacements.forEach(({ className, style }) => {
            // Check if element already has a style attribute
            const styleRegex = new RegExp(`class="${className}"(\\s+style="([^"]*)")?`, 'g');
            modifiedTemplateInfo = modifiedTemplateInfo.replace(styleRegex, (match, existingStyleAttr, existingStyles) => {
                if (existingStyles) {
                    // Append new styles to existing ones
                    const combinedStyles = existingStyles + '; ' + style;
                    return `class="${className}" style="${combinedStyles}"`;
                } else {
                    // Add new style attribute
                    return `class="${className}" style="${style}"`;
                }
            });
        });

        return (
            <>
                <div className={styles.template_preview_rendered}>
                    <div
                        className={styles.template_preview_renderedContent}
                        dangerouslySetInnerHTML={{
                            __html: modifiedTemplateInfo,
                        }}
                    />
                </div>
                <AIEduButton
                    label={f({ id: 'AI_EDU_OK' })}
                    className='btnPrimary mt-4'
                    style={{ margin: '0 auto' }}
                    onClick={hideTemplatePreviewDialog}
                />
            </>
        );
    }, [
        templates,
        selectedTemplate,
        selectedBackgroundColor,
        selectedTextColor,
    ]);

    const handleSave = async () => {
        // if (!organizationId) {
        //     showErrorToast(
        //         f({ id: 'AI_EDU_ORGANIZATION_ID_REQUIRED_TO_SAVE' })
        //     );
        //     return;
        // }

        // Validate required fields
        if (!alertTitleText.trim()) {
            showErrorToast(f({ id: 'AI_EDU_ALERT_TITLE_REQUIRED' }));
            return;
        }

        if (!selectedProvider) {
            showErrorToast(f({ id: 'AI_EDU_PROVIDER_REQUIRED' }));
            return;
        }

        if (!selectedTemplate) {
            showErrorToast(f({ id: 'AI_EDU_TEMPLATE_REQUIRED' }));
            return;
        }

        if (!selectedBackgroundColor) {
            showErrorToast(f({ id: 'AI_EDU_BACKGROUND_COLOR_REQUIRED' }));
            return;
        }

        if (!selectedTextColor) {
            showErrorToast(f({ id: 'AI_EDU_TEXT_COLOR_REQUIRED' }));
            return;
        }

        setLoading(true);
        try {
            const payload = {
                title: alertTitleText?.trim(),
                description: descriptionText?.trim(),
                alertProvider: selectedProvider?.code,
                xmlBody:
                    selectedProvider?.code === 'raptor'
                        ? xmlBodyText?.trim()
                        : '',
                templateId: selectedTemplate,
                bgColor: selectedBackgroundColor?.code,
                textColor: selectedTextColor?.code,
                tagIds: selectedTags.map(tag => tag.tagId || tag.id),
                operator: selectedOperator.toUpperCase()
            };

            const result = await API.createNewAlert(organizationId, payload);
            if (result.status === 201) {
                showSuccessToast(result.data.message);
                triggerRefresh('alert');
                
                // Get the newly created alert ID from the response
                const newAlertId = result.data.alert?.alertId;
                if (newAlertId) {
                    const { webHookUrl, apiKey } = result.data?.alert ?? {};
                    setWebhookUrl(webHookUrl || '');
                    setApiKey(apiKey || '');
                }
                setShowTagManagementPopup(true);
            } else {
                showErrorToast(f({ id: 'AI_EDU_FAILED_TO_CREATE_EMERGENCY_ALERT' }));
            }
        } catch (error) {
            showErrorToast(f({ id: 'AI_EDU_ERROR_CREATING_EMERGENCY_ALERT' }));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(EMERGENCY_ALERT_PATH);
        setIsDescriptionEditMode(false);
        setIsAlertTitleEditMode(false);
        setDescriptionText('');
        // Reset form state
        setSelectedProvider(isCreateMode ? PROVIDERS[0] : null);
        setSelectedAlertType(null);
        setXmlBodyText('');
        setSelectedBackgroundColor({ name: 'White', code: '#FFFFFF' });
        setSelectedTextColor({ name: 'Black', code: '#000000' });
        setSelectedTemplate(null);
        setShowTemplatePreview(false);
        setSelectedTags([]);
        setSelectedOperator('or');
        setWebhookUrl('');
        setApiKey('');
        triggerRefresh('alert');
    };

    const handleDelete = () => {
        if (isEditMode) {
            // In edit mode, show delete confirmation dialog for single alert
            setSelectedAlertsList([{ alertId: id }]);
            setShowDeleteAlertDialog(true);
        } else {
            // In list mode, use bulk delete
            if (selectedAlertsList.length === 0) {
                showErrorToast(
                    f({ id: 'AI_EDU_SELECT_AT_LEAST_ONE_EMERGENCY_ALERT' })
                );
                return;
            }
            setShowDeleteAlertDialog(true);
        }
    };

    const hideDeleteAlertDialog = () => {
        setShowDeleteAlertDialog(false);
    };

    const deleteSelectedAlerts = async () => {
        if (!organizationId) {
            showErrorToast(
                f({ id: 'AI_EDU_ORGANIZATION_ID_REQUIRED_TO_SAVE' })
            );
            return;
        }

        setDeleteLoading(true);
        try {
            const alertIds = selectedAlertsList.map(
                (alert) => alert.alertId || alert.id || alert._id
            );

            // Use bulk delete API
            await API.deleteAlerts(organizationId, { alertIds });

            showSuccessToast(
                f(
                    { id: 'AI_EDU_SUCCESSFULLY_DELETED_EMERGENCY_ALERTS' },
                    { count: alertIds.length }
                )
            );
            setSelectedAlertsList([]);
            setShowDeleteAlertDialog(false);

            // If in edit mode, navigate back to list and refresh
            if (isEditMode) {
                navigate(EMERGENCY_ALERT_PATH);
                triggerRefresh('alert');
            } else {
                // In list mode, refresh the alerts list
                await fetchAlerts(Math.floor(first / rows), rows, sortBy, sortOrder, searchTerm);
            }
        } catch (error) {
            showErrorToast(f({ id: 'AI_EDU_ERROR_DELETING_EMERGENCY_ALERTS' }));
        } finally {
            setDeleteLoading(false);
        }
    };

    const getDeleteMessage = () => {
        const count = selectedAlertsList.length;
        return count === 1
            ? f({ id: 'AI_EDU_DELETE_EMERGENCY_ALERT_CONFIRMATION' })
            : f(
                  {
                      id: 'AI_EDU_DELETE_MULTIPLE_EMERGENCY_ALERTS_CONFIRMATION',
                  },
                  { count }
              );
    };

    const handleSaveInEditMode = async () => {
        if (!organizationId || !id) {
            showErrorToast(
                f({ id: 'AI_EDU_ORGANIZATION_ID_AND_ALERT_ID_REQUIRED' })
            );
            return;
        }

        // Validate required fields
        if (!alertTitleText.trim()) {
            showErrorToast(f({ id: 'AI_EDU_ALERT_TITLE_REQUIRED' }));
            return;
        }

        if (!selectedProvider) {
            showErrorToast(f({ id: 'AI_EDU_PROVIDER_REQUIRED' }));
            return;
        }

        setLoading(true);
        try {
            const payload = {
                title: alertTitleText?.trim(),
                description: descriptionText?.trim(),
                alertProvider: selectedProvider?.code,
                xmlBody:
                    selectedProvider?.code === 'raptor'
                        ? xmlBodyText?.trim()
                        : '',
                templateId: selectedTemplate,
                bgColor: selectedBackgroundColor?.code,
                textColor: selectedTextColor?.code,
                tagIds: selectedTags.map(tag => tag.tagId || tag.id),
                operator: selectedOperator.toUpperCase()
            };

            const result = await API.updateAlert(organizationId, id, payload);
            if (result.status === 200) {
                showSuccessToast(f({ id: 'AI_EDU_EMERGENCY_ALERT_UPDATED_SUCCESSFULLY' }));
                triggerRefresh('alert');
                navigate(EMERGENCY_ALERT_PATH);
            } else {
                showErrorToast(f({ id: 'AI_EDU_ERROR_UPDATING_EMERGENCY_ALERT' }));
            }
        } catch (error) {
            showErrorToast(f({ id: 'AI_EDU_ERROR_UPDATING_EMERGENCY_ALERT' }));
        } finally {
            setLoading(false);
        }
    };

    const handleTagPopupConfirm = async () => {
        setShowTagManagementPopup(false);
        setLoading(true);
        try {
            const payload = {
                title: alertTitleText?.trim(),
                description: descriptionText?.trim(),
                alertProvider: selectedProvider?.code,
                xmlBody:
                    selectedProvider?.code === 'raptor'
                        ? xmlBodyText?.trim()
                        : '',
                templateId: selectedTemplate,
                bgColor: selectedBackgroundColor?.code,
                textColor: selectedTextColor?.code,
                tagIds: selectedTags.map(tag => tag.tagId || tag.id),
                operator: selectedOperator.toUpperCase()
            };

            let result;
            if (pendingSaveAction === 'create') {
                result = await API.createNewAlert(organizationId, payload);
                if (result.status === 201) {
                    showSuccessToast(result.data.message);
                    triggerRefresh('alert');
                    navigate(EMERGENCY_ALERT_PATH);
                } else {
                    showErrorToast(f({ id: 'AI_EDU_FAILED_TO_CREATE_EMERGENCY_ALERT' }));
                }
            } else if (pendingSaveAction === 'update') {
                result = await API.updateAlert(organizationId, id, payload);
                if (result.status === 200) {
                    showSuccessToast(f({ id: 'AI_EDU_EMERGENCY_ALERT_UPDATED_SUCCESSFULLY' }));
                    triggerRefresh('alert');
                    navigate(EMERGENCY_ALERT_PATH);
                } else {
                    showErrorToast(f({ id: 'AI_EDU_ERROR_UPDATING_EMERGENCY_ALERT' }));
                }
            }
        } catch (error) {
            if (pendingSaveAction === 'create') {
                showErrorToast(f({ id: 'AI_EDU_ERROR_CREATING_EMERGENCY_ALERT' }));
            } else {
                showErrorToast(f({ id: 'AI_EDU_ERROR_UPDATING_EMERGENCY_ALERT' }));
            }
        } finally {
            setLoading(false);
            setPendingSaveAction(null);
        }
    };

    const handleTagPopupCancel = () => {
        setShowTagManagementPopup(false);
        setPendingSaveAction(null);
        // Navigate back to list after successful create and popup close
        navigate(EMERGENCY_ALERT_PATH);
    };

    const handleDescriptionEdit = () => {
        setIsDescriptionEditMode(true);
    };

    const handleTitleEdit = () => {
        setIsAlertTitleEditMode(true);
    };

    const handleRegenerateApiKey = () => {
        setShowRegenerateKeyDialog(true);
    };

    const handleConfirmRegenerateKey = async () => {
        if (!organizationId || !id) {
            showErrorToast(
                f({ id: 'AI_EDU_ORGANIZATION_ID_AND_ALERT_ID_REQUIRED' })
            );
            return;
        }

        try {
            const result = await API.regenerateApiKey(organizationId, id);
            if (result.status === 200) {
                // After successful regeneration, fetch the updated alert data to get the new API key
                const alertResult = await API.getAlertById(organizationId, id);
                if (alertResult.status === 200) {
                    const { apiKey: generatedKey } = alertResult.data || {};
                    setApiKey(generatedKey);
                    showSuccessToast(f({ id: 'AI_EDU_API_KEY_REGENERATED_SUCCESSFULLY' }));
                } else {
                    showErrorToast(f({ id: 'AI_EDU_FAILED_TO_FETCH_UPDATED_ALERT_DATA' }));
                }
            } else {
                showErrorToast(f({ id: 'AI_EDU_FAILED_TO_REGENERATE_API_KEY' }));
            }
        } catch (error) {
            showErrorToast(f({ id: 'AI_EDU_ERROR_REGENERATING_API_KEY' }));
        } finally {
            setShowRegenerateKeyDialog(false);
        }
    };

    const handleCancelRegenerateKey = () => {
        setShowRegenerateKeyDialog(false);
    };

    // Pagination event handlers
    const onPageChange = useCallback((event) => {
        setFirst(event.first);
        setRows(event.rows);
    }, []);

    const onSort = useCallback((event) => {
        setSortBy(event.sortField);
        setSortOrder(event.sortOrder);
    }, []);

    const handleAlertSearch = useCallback(() => {
        setFirst(0);
        setShouldSearch((prev) => !prev); // Toggle to trigger search
    }, []);

    // Handle search term change to detect when search is cleared
    useEffect(() => {
        if (searchTerm === '' && isListMode && organizationId) {
            // When search is cleared, trigger a search to load default data
            setShouldSearch((prev) => !prev);
        }
    }, [searchTerm, isListMode, organizationId]);

    const handleInputKeyDown = useCallback(
        (event) => {
            if (event.key === 'Enter') {
                handleAlertSearch();
            }
        },
        [handleAlertSearch]
    );

    const onSearch = useCallback(() => {
        handleAlertSearch();
    }, [handleAlertSearch]);

    const colorItemTemplate = useCallback((option) => {
        return (
            <div className={styles.color_option_item}>
                <span
                    className={styles.color_preview}
                    style={{ backgroundColor: option.code }}
                />
                <span>{option.name}</span>
            </div>
        );
    }, []);

    const selectedColorTemplate = useCallback((option, props) => {
        if (option) {
            return (
                <div className={styles.selected_color_item}>
                    <span
                        className={styles.color_preview}
                        style={{ backgroundColor: option.code }}
                    />
                    <span>{option.name}</span>
                </div>
            );
        }
        return <span>{props.placeholder}</span>;
    }, []);

    const EmergencyAlertActionBody = useCallback(
        (rowData) => {
            const handleDeleteAlert = (e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedAlertsList([rowData]);
                setShowDeleteAlertDialog(true);
            };

            const handleEditAlert = (e) => {
                e.preventDefault();
                e.stopPropagation();
                const alertId = rowData.alertId || rowData.id || rowData._id;
                if (alertId) {
                    navigate(`${EMERGENCY_ALERT_PATH}/${alertId}`);
                } else {
                    console.error('No alertId found in row data:', rowData);
                }
            };

            return (
                <div className='flex gap-4'>
                    <i
                        className='pi pi-pencil cursor-pointer mr-1'
                        style={{ fontSize: '20px' }}
                        onClick={handleEditAlert}
                    />
                    <i
                        className='pi pi-trash cursor-pointer'
                        style={{ fontSize: '20px' }}
                        onClick={handleDeleteAlert}
                    />
                </div>
            );
        },
        [setSelectedAlertsList, setShowDeleteAlertDialog, navigate]
    );

    const formatDateBody = useCallback((rowData) => {
        return !!rowData.updatedAt
            ? formatDateWithTimeStamp(rowData.updatedAt)
            : '--';
    }, []);

    const AlertProviderBody = useCallback((rowData) => PROVIDERS_MAP[rowData.alertProvider], []);
    const AlertTagsBody = useCallback((rowData) => rowData?.tags?.map(({ tagName }) => tagName)?.join(', ') || '--', []);

    // Memoized table column configuration to prevent recreation on every render
    const TABLE_COLUMNS = useMemo(() => [
        {
            field: 'title',
            header: f({ id: 'AI_EDU_ALERT' }),
            width: '25%',
            minWidth: '15rem',
            sortable: true,
        },
        {
            field: 'tags',
            header: 'Tags',
            width: '20%',
            minWidth: '10rem',
            sortable: true,
            body: AlertTagsBody,
        },
        {
            field: 'alertProvider',
            header: f({ id: 'AI_EDU_ALERT_PROVIDER' }),
            width: '20%',
            minWidth: '10rem',
            sortable: true,
            body: AlertProviderBody,
        },
        {
            header: f({ id: 'AI_EDU_UPDATED_DATE' }),
            field: 'updatedAt',
            width: '20%',
            minWidth: '10rem',
            sortable: true,
            body: formatDateBody,
        },
        {
            header: f({ id: 'AI_EDU_ACTION' }),
            minWidth: '5rem',
            body: EmergencyAlertActionBody,
        },
    ], [AlertProviderBody, formatDateBody, EmergencyAlertActionBody, f]);

    return (
        <>
        {(alertDataLoading || alertsLoading) && <AIEduSpinner /> }
        <div className={styles.emergency_alert}>
            {isListMode ? (
                <>
                    <PageTitle
                        title={f({ id: 'AI_EDU_EMERGENCY_ALERT' })}
                        count={totalRecords}
                        showCount={alerts.length > 0}
                    />
                    <EmergencyAlertList
                        data={alerts}
                        rows={rows}
                        first={first}
                        onSort={onSort}
                        sortBy={sortBy}
                        onSearch={onSearch}
                        sortOrder={sortOrder}
                        columns={TABLE_COLUMNS}
                        searchTerm={searchTerm}
                        onDelete={handleDelete}
                        onPageChange={onPageChange}
                        totalRecords={totalRecords}
                        setSearchTerm={setSearchTerm}
                        selectedAlertsList={selectedAlertsList}
                        handleInputKeyDown={handleInputKeyDown}
                        setSelectedAlertsList={setSelectedAlertsList}
                        onAddNew={() => navigate(EMERGENCY_ALERT_CREATE_PATH)}
                    />
                </>
            ) : (
                <>
                    <div className={styles.emergency_alert_header}>
                        <span
                            onClick={() => navigate(EMERGENCY_ALERT_PATH)}
                            className='cursor-pointer hover:underline'
                        >
                            {f({ id: 'AI_EDU_EMERGENCY_ALERT' })}
                        </span>
                        <i className='pi pi-chevron-right mx-2' />
                        <span>
                            {isEditMode
                                ? f({ id: 'AI_EDU_EDIT' })
                                : f({ id: 'AI_EDU_CREATE' })}
                        </span>
                    </div>
                    <>
                        <EmergencyAlertForm
                            isEditMode={isEditMode}
                            isAlertTitleEditMode={isAlertTitleEditMode}
                            isDescriptionEditMode={isDescriptionEditMode}
                            alertTitleText={alertTitleText}
                            descriptionText={descriptionText}
                            selectedProvider={selectedProvider}
                            selectedAlertType={selectedAlertType}
                            xmlBodyText={xmlBodyText}
                            providers={PROVIDERS}
                            alertTypes={ALERT_TYPES}
                            onTitleEdit={handleTitleEdit}
                            onDescriptionEdit={handleDescriptionEdit}
                            onProviderChange={(e) => {
                                setSelectedProvider(e.value);
                                // Show webhook popup when provider changes in edit mode
                                if (isEditMode) {
                                    setPendingSaveAction('update');
                                    setShowTagManagementPopup(true);
                                }
                            }}
                            onAlertTypeChange={(e) =>
                                setSelectedAlertType(e.value)
                            }
                            onXmlBodyChange={(e) =>
                                setXmlBodyText(e.target.value)
                            }
                            onTitleChange={(e) =>
                                setAlertTitleText(e.target.value)
                            }
                            onDescriptionChange={(e) =>
                                setDescriptionText(e.target.value)
                            }
                            onSave={
                                isEditMode ? handleSaveInEditMode : handleSave
                            }
                            onDelete={handleDelete}
                            f={f}
                            webhookUrl={webhookUrl}
                            apiKey={apiKey}
                            onCopyWebhookUrl={() => navigator.clipboard.writeText(webhookUrl || '')}
                            onCopyApiKey={() => navigator.clipboard.writeText(apiKey || '')}
                            onRegenerateApiKey={handleRegenerateApiKey}
                        />

                        <EmergencyAlertMetadata
                            isEditMode={isEditMode}
                            alertData={currentAlertData}
                            f={f}
                        />

                        <div className={`${styles.horizontalRule} mt-4 mb-4`} />

                        <div>
                            <div
                                className={`mt-4 mb-4 ${styles.emergency_alert_textStyle}`}
                            >
                                {f({ id: 'AI_EDU_DISPLAY_SETTINGS' })}
                            </div>
                            <div
                                className={`mt-4 mb-4 ${styles.emergency_alert_textStyle2}`}
                            >
                                {f({ id: 'AI_EDU_TEMPLATE' })}
                            </div>

                            <TemplateGrid
                                templates={templates}
                                selectedTemplate={selectedTemplate}
                                onTemplateSelect={handleTemplateSelect}
                                selectedBackgroundColor={
                                    selectedBackgroundColor
                                }
                                selectedTextColor={selectedTextColor}
                                showTemplatePreview={false}
                                colorOptions={COLOR_OPTIONS}
                                selectedColorTemplate={selectedColorTemplate}
                                colorItemTemplate={colorItemTemplate}
                                onBackgroundColorChange={(e) =>
                                    setSelectedBackgroundColor(e.value)
                                }
                                onTextColorChange={(e) =>
                                    setSelectedTextColor(e.value)
                                }
                                isLoading={templatesLoading}
                                f={f}
                            />
                        </div>

                        <div className={`${styles.horizontalRule} mt-4 mb-4`} />
                                                
                            <TagManagement
                                f={f} 
                                selectedTags={selectedTags}
                                setSelectedTags={setSelectedTags}
                                selectedOperator={selectedOperator}
                                setSelectedOperator={setSelectedOperator}
                            />

                        <div className={styles.action_buttons}>
                            <AIEduButton
                                label={f({ id: 'AI_EDU_CANCEL' })}
                                className='btnSecondary'
                                onClick={handleCancel}
                            />
                            {selectedTemplate && (
                                <AIEduButton
                                    label={f({ id: 'AI_EDU_TEMPLATE_PREVIEW' })}
                                    icon={
                                        showTemplatePreview
                                            ? 'pi pi-eye-slash'
                                            : 'pi pi-eye'
                                    }
                                    className='btnPrimary'
                                    onClick={handleTemplatePreview}
                                />
                            )}
                            <AIEduButton
                                label={f({ id: 'AI_EDU_SAVE' })}
                                icon='pi pi-save'
                                className='btnPrimary'
                                onClick={
                                    isEditMode
                                        ? handleSaveInEditMode
                                        : handleSave
                                }
                                loading={loading}
                                disabled={loading}
                            />
                        </div>
                    </>
                </>
            )}

            <AIEduDialog
                visible={showTemplatePreview}
                onHide={hideTemplatePreviewDialog}
                title={TemplatePreviewTitle}
                content={getTemplatePreviewContent}
                style={{ width: '60vw', maxWidth: '800px' }}
                draggable={false}
                breakpoints={{ '960px': '75vw', '640px': '90vw' }}
            />

            
            <DeleteEmergencyAlertDialog
                visible={showDeleteAlertDialog}
                hideDeleteAlertDialog={hideDeleteAlertDialog}
                deleteSelectedAlerts={deleteSelectedAlerts}
                deleteMessage={getDeleteMessage()}
            />

            <TagManagementPopup
                visible={showTagManagementPopup}
                onHide={handleTagPopupCancel}
                onConfirm={handleTagPopupConfirm}
                loading={loading}
                f={f}
                webhookUrl={webhookUrl}
                apiKey={apiKey}
                isEditMode={isEditMode}
            />

            <RegenerateKeyDialog
                visible={showRegenerateKeyDialog}
                onHide={handleCancelRegenerateKey}
                onConfirm={handleConfirmRegenerateKey}
            />

            <AIEduToast ref={toastRef} />
        </div>
        </>
    );
};

export default EmergencyAlert;
